import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AdPlacement {
  id: string;
  name: string;
  slot_id: string;
  is_enabled: boolean | null;
  ad_type: string | null;
  custom_code: string | null;
  image_url: string | null;
  link_url: string | null;
  open_in_new_tab: boolean | null;
  device_targeting: string | null;
  start_date: string | null;
  end_date: string | null;
  priority: number | null;
  recommended_dimensions: string | null;
  adsense_slot_id: string | null;
  adsense_format: string | null;
  adsense_layout: string | null;
  adsense_layout_key: string | null;
  placement_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface PopupAd {
  id: string;
  name: string;
  is_enabled: boolean | null;
  popup_type: string | null;
  content_type: string | null;
  ad_code: string | null;
  image_url: string | null;
  link_url: string | null;
  open_in_new_tab: boolean | null;
  frequency: string | null;
  trigger_type: string | null;
  trigger_value: number | null;
  device_targeting: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
}

// Fetch all ad placements for CMS
export function useAdPlacements() {
  return useQuery({
    queryKey: ['ad-placements-all'],
    queryFn: async (): Promise<AdPlacement[]> => {
      const { data, error } = await supabase
        .from('ad_placements')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching ad placements:', error);
        throw error;
      }

      return (data || []) as AdPlacement[];
    },
    staleTime: 10 * 1000,
  });
}

// Update an ad placement
export function useUpdateAdPlacement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (placement: Partial<AdPlacement> & { id: string }) => {
      const { id, ...updates } = placement;
      const { error } = await supabase
        .from('ad_placements')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-placements-all'] });
      queryClient.invalidateQueries({ queryKey: ['ad-placements'] });
      queryClient.invalidateQueries({ queryKey: ['active-ad-placements-all'] });
      toast.success('Ad placement updated');
    },
    onError: (error) => {
      toast.error('Failed to update ad placement');
      console.error('Error:', error);
    },
  });
}

// Fetch all popup ads
export function usePopupAds() {
  return useQuery({
    queryKey: ['popup-ads'],
    queryFn: async (): Promise<PopupAd[]> => {
      const { data, error } = await supabase
        .from('popup_ads' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching popup ads:', error);
        throw error;
      }

      return (data || []) as unknown as PopupAd[];
    },
    staleTime: 10 * 1000,
  });
}

// Create a popup ad
export function useCreatePopupAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (popupAd: Partial<PopupAd>) => {
      const { error } = await supabase
        .from('popup_ads' as any)
        .insert(popupAd);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popup-ads'] });
      toast.success('Popup ad created');
    },
    onError: (error) => {
      toast.error('Failed to create popup ad');
      console.error('Error:', error);
    },
  });
}

// Update a popup ad
export function useUpdatePopupAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (popupAd: Partial<PopupAd> & { id: string }) => {
      const { id, ...updates } = popupAd;
      const { error } = await supabase
        .from('popup_ads' as any)
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popup-ads'] });
      toast.success('Popup ad updated');
    },
    onError: (error) => {
      toast.error('Failed to update popup ad');
      console.error('Error:', error);
    },
  });
}

// Delete a popup ad
export function useDeletePopupAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('popup_ads' as any)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popup-ads'] });
      toast.success('Popup ad deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete popup ad');
      console.error('Error:', error);
    },
  });
}

// Get active popup ad for frontend (respects enabled, scheduling, device)
export function useActivePopupAd() {
  return useQuery({
    queryKey: ['active-popup-ad'],
    queryFn: async (): Promise<PopupAd | null> => {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('popup_ads' as any)
        .select('*')
        .eq('is_enabled', true)
        .or(`start_date.is.null,start_date.lte.${now}`)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching active popup:', error);
        return null;
      }

      return data as unknown as PopupAd | null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Batch-fetch ALL active ad placements in one query, then filter client-side per slot.
// This eliminates N+1 queries when many ad slots are on one page.
function useAllActiveAdPlacements() {
  return useQuery({
    queryKey: ['active-ad-placements-all'],
    queryFn: async (): Promise<AdPlacement[]> => {
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('ad_placements')
        .select('*')
        .eq('is_enabled', true)
        .or(`start_date.is.null,start_date.lte.${now}`)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('priority', { ascending: false });

      if (error) {
        console.error('Error fetching ad placements:', error);
        return [];
      }

      return (data || []) as AdPlacement[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — ads rarely change
  });
}

// Get active ad for a specific slot — filters from the batch cache (no extra query)
export function useActiveAdPlacement(slotId: string) {
  const { data: allPlacements, isLoading } = useAllActiveAdPlacements();

  const placement = allPlacements?.find(p => p.slot_id === slotId) ?? null;

  return { data: placement, isLoading };
}
